import React from "react";
import PropTypes from "prop-types";

import { useParams } from "react-router";
import { Link } from "react-router-dom";

import {
    CCT_CONSOLE,
    BILANCIO_SAAS,
    TS_PAY_URL,
    TS_HOSPITALITY_URL,
    HR_URL,
    COMMON_LOGIN_API_URL,
    ARCHIVE_WEBAPP_URL,
    TCS_APP_ID,
    TRC_APP_ID,
    TVC_APP_ID,
    TIGRAN_URL
} from "../../../config";
import { getCurrentCookieConsentsCsv } from "../../../utils/cookie-consent-utils";
import { useFeature } from "../../../hooks/use-feature";
import generateJumpUrlWithContext from "../../../utils/generate-jump-url-with-context";

/** @typedef {{ isExternal?: boolean, link?: string, jump?: { appId: string, registryServiceId: string, state?: {[key: string]: any}, deepLink?: string, deepLinkParameters?: {[key: string]: any} }, sendToken?: boolean, cookieConsents?: boolean }} AppLinkData */
/** @type {{ [key: string]: AppLinkData }} */
const appLinks = {
    ARC: { isExternal: true, link: CCT_CONSOLE },
    AML: { link: "/aml" },
    ANA: { link: "/analytics" },
    DBX: { link: "/digitalbox" },
    DRAG: { link: "/drag" },
    EIP: { link: "/fatturazione" },
    "GDPR-LOG": { link: "/gdprlog" },
    PECMANAGER: { link: "/pecmanager" },
    PPL: { link: "/people-app" },
    PRIVACY: { link: "/privacy" },
    SIG: { link: "/firma" },
    TEL: { link: "/adempimenti" },
    BDG: { isExternal: true, link: BILANCIO_SAAS, sendToken: true, cookieConsents: true },
    EIT: { link: "/invoice-trading" },
    ESM: { link: "/spendmanagement" },
    CRAM: { link: "/cram-front" },
    AGRO: { link: "/agro" },
    NIUMABONUS: { link: "/niumabonus" },
    EBS: { isExternal: true, link: TS_PAY_URL },
    SPID: { link: "/spid" },
    BIS: { link: "/business-information" },
    DDL: { link: "/deadlines" },
    TSH: { isExternal: true, link: TS_HOSPITALITY_URL },
    HR: { isExternal: true, link: HR_URL },
    SCF: { link: "/deeptier" },
    CYBER: { link: "/cyber" },
    BI: { link: "/business-intelligence" },
    FIN: { link: "/fin" },
    TCS: { jump: { appId: TCS_APP_ID, registryServiceId: "schedulog-app", label: "Sicurezza Cantieri" } },
    TRC: { jump: { appId: TRC_APP_ID, registryServiceId: "termolog-app" } },
    TVC: { jump: { appId: TVC_APP_ID, registryServiceId: "travilog-app" } },
    TIGRAN: { isExternal: true, sendWorkspace: true, link: TIGRAN_URL }
};

export const AppLink = ({ children, companyStatus, serviceId, services, token, item }) => {
    const params = useParams();
    const newBilancioFeature = useFeature("NEW_BILANCIO");

    /** @type {AppLinkData} */
    let link = {};

    if (serviceId === "ARC" && services.find(x => x.id === "ARC" && x.activeForItem)) {
        link = { isExternal: true, link: ARCHIVE_WEBAPP_URL };
    } else if (appLinks[serviceId]) {
        link = appLinks[serviceId];
    } else {
        link = {};
    }

    if (link.isExternal || link.jump) {
        /** @type {string | undefined} */
        let extLink = link.link;
        const cookieCsv = getCurrentCookieConsentsCsv();

        if (link.jump) {
            extLink = generateJumpUrlWithContext(
                item,
                item?.workspaceId,
                link.jump.appId,
                link.jump.registryServiceId,
                link.jump.state,
                link.jump.deepLink || "/",
                link.jump.deepLinkParameters || {},
                link.jump.label
            );
        } else if (serviceId === "BDG" && newBilancioFeature) {
            const bdgDeepLinkParams = new URLSearchParams({
                cookieConsents: cookieCsv,
                ...(item?.workspaceId ? { workspaceId: item.workspaceId } : {})
            });
            const jumpParams = new URLSearchParams({
                serviceId: "pro:bilancio:bilancio",
                deepLink: `?${bdgDeepLinkParams}`
            });
            extLink = `${COMMON_LOGIN_API_URL}/jump?${jumpParams}`;
        } else {
            const deepLinkParams = new URLSearchParams();

            if (link.sendToken) {
                deepLinkParams.append("securityToken", token);
            }

            if (link.cookieConsents) {
                deepLinkParams.append("cookieConsents", cookieCsv);
            }

            if (link.sendWorkspace && item?.workspaceId) {
                deepLinkParams.append("workspaceId", item.workspaceId);
            }

            if (deepLinkParams.toString()) {
                extLink = `${link.link}?${deepLinkParams.toString()}`;
            }
        }

        return (
            <a
                href={extLink}
                rel="noopener noreferrer"
                target="_blank"
                style={{
                    // disabled click when companyStatus is not CERTIFIED for SPID app. temporary fix.
                    pointerEvents: companyStatus !== "CERTIFIED" && serviceId === "SPID" && "none"
                }}
            >
                {children}
            </a>
        );
    } else {
        return (
            <Link
                to={`/${params.item}/apps${link.link}`}
                style={{
                    // disabled click when companyStatus is not CERTIFIED for SPID app. temporary fix.
                    pointerEvents: companyStatus !== "CERTIFIED" && serviceId === "SPID" && "none"
                }}
            >
                {children}
            </Link>
        );
    }
};

AppLink.propTypes = {
    children: PropTypes.any,
    companyStatus: PropTypes.string,
    serviceId: PropTypes.string.isRequired,
    services: PropTypes.array,
    token: PropTypes.string,
    item: PropTypes.object
};

export default AppLink;
